import { useEffect, useRef, useState } from 'react'
import { CloseIcon, SearchIcon, TrashIcon } from './icons.jsx'
import Select from './Select.jsx'

// Column organizer — pick which columns show, reorder the visible ones (drag),
// and set rows-per-page. Modeled on the reference "Select columns to display".
// allColumns: [{ key, label }]; visibleKeys: ordered string[]; onSave receives
// the new { visibleKeys, pageSize }.
export default function ColumnsModal({
  allColumns,
  visibleKeys,
  pageSize,
  pageSizeOptions = [30, 50, 100, 200],
  onSave,
  onClose,
}) {
  const [selected, setSelected] = useState(() => visibleKeys.filter((k) => allColumns.some((c) => c.key === k)))
  const [search, setSearch] = useState('')
  const [size, setSize] = useState(pageSize || pageSizeOptions[0])

  // Pointer-driven reorder (no native HTML5 drag): the grabbed row is lifted
  // out of flow and follows the cursor as an absolutely-positioned card, the
  // drop slot is computed from geometry (row height + gap), and the neighbors
  // slide out of the way with an animated transform. Committed on release.
  const listRef = useRef(null)
  const dragMeta = useRef(null) // { pointerId, grabOffsetY, slotH, height }
  const [drag, setDrag] = useState(null) // { from, to, y } | null

  const labelOf = (key) => allColumns.find((c) => c.key === key)?.label || key
  const selectedSet = new Set(selected)
  const q = search.trim().toLowerCase()
  const filteredAll = allColumns.filter((c) => !q || c.label.toLowerCase().includes(q))

  function toggle(key) {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  const ROW_GAP = 6 // matches the row's mb-1.5

  function dragY(e) {
    const list = listRef.current
    const rect = list.getBoundingClientRect()
    const { grabOffsetY, height } = dragMeta.current
    // Top of the floating row within the scroll content, clamped to the list.
    const raw = e.clientY - rect.top + list.scrollTop - grabOffsetY
    return Math.max(0, Math.min(raw, list.scrollHeight - height))
  }

  function onHandleDown(e, index) {
    const row = e.currentTarget.closest('[data-colrow]')
    if (!row || e.button !== 0) return
    e.preventDefault()
    const height = row.offsetHeight
    dragMeta.current = {
      grabOffsetY: e.clientY - row.getBoundingClientRect().top,
      slotH: height + ROW_GAP,
      height,
    }
    setDrag({ from: index, to: index, y: dragY(e) })
  }

  // Track the pointer at the window level while a row is held, so the drag
  // never drops out when the cursor leaves the handle or the list.
  useEffect(() => {
    if (!drag) return
    function onMove(e) {
      const y = dragY(e)
      const { slotH } = dragMeta.current
      const to = Math.max(0, Math.min(Math.round(y / slotH), selected.length - 1))
      setDrag((d) => (d && (d.y !== y || d.to !== to) ? { ...d, y, to } : d))
    }
    function onUp() {
      setDrag((d) => {
        if (d && d.from !== d.to) {
          setSelected((prev) => {
            const next = [...prev]
            const [moved] = next.splice(d.from, 1)
            next.splice(d.to, 0, moved)
            return next
          })
        }
        return null
      })
      dragMeta.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null, selected.length])

  // While dragging, neighbors between the origin and the target slot slide by
  // one row so the gap visually tracks the drop position.
  function rowShift(i) {
    if (!drag || i === drag.from) return 'translateY(0)'
    const { from, to } = drag
    const slotH = dragMeta.current?.slotH || 0
    if (from < to && i > from && i <= to) return `translateY(-${slotH}px)`
    if (from > to && i >= to && i < from) return `translateY(${slotH}px)`
    return 'translateY(0)'
  }

  const sizeOptions = pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(0,0,0,0.5)] p-6" onClick={onClose}>
      <div
        className="flex max-h-[86vh] w-full max-w-[860px] flex-col overflow-hidden rounded-[16px] border border-line bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-x-3 [border-bottom:1px_solid_var(--border)] px-5 py-4">
          <h3 className="m-0 text-[16px] font-semibold text-ink">Select columns to display</h3>
          <button
            type="button"
            className="grid h-8 w-8 place-items-center rounded-[8px] text-faint transition-colors hover:bg-panel hover:text-ink"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-2 gap-5 overflow-hidden px-5 py-4">
          {/* All columns */}
          <div className="flex min-h-0 flex-col">
            <div className="mb-2 text-[13px] font-semibold text-dim">All columns</div>
            <div className="relative mb-2">
              <SearchIcon className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-faint" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search columns…"
                className="w-full rounded-[9px] border border-line bg-panel py-[9px] pl-[33px] pr-3 text-[14px] text-ink focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] focus:outline-none"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {filteredAll.map((c) => {
                const checked = selectedSet.has(c.key)
                return (
                  <label
                    key={c.key}
                    className="flex cursor-pointer items-center gap-x-2.5 rounded-[8px] px-2 py-2 text-[14px] text-ink hover:bg-panel"
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggle(c.key)}
                    />
                    {/* Animated checkbox: box tints in, check scales in. */}
                    <span
                      aria-hidden
                      className={`grid h-[16px] w-[16px] flex-none place-items-center rounded-[4px] border transition-colors duration-150 ${
                        checked ? 'border-primary bg-primary' : 'border-faint bg-transparent'
                      }`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`h-[11px] w-[11px] text-white transition-[transform,opacity] duration-150 ease-out ${
                          checked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                        }`}
                      >
                        {/* coolicons: line/Check.svg */}
                        <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {c.label}
                  </label>
                )
              })}
              {filteredAll.length === 0 && (
                <div className="px-2 py-3 text-[13px] text-faint">No columns match.</div>
              )}
            </div>
          </div>

          {/* Selected columns (reorderable) */}
          <div className="flex min-h-0 flex-col">
            <div className="mb-2 flex items-center gap-x-2 text-[13px] font-semibold text-dim">
              Selected columns
              <span className="rounded-full bg-primary-tint px-2 py-px text-[11px] font-semibold text-primary-dark">
                {selected.length}
              </span>
            </div>
            <div ref={listRef} className="relative min-h-0 flex-1 overflow-y-auto pr-1">
              {selected.map((key, i) => {
                const isDragged = drag?.from === i
                return (
                  <div
                    key={key}
                    data-colrow
                    style={{
                      // The grabbed row keeps its slot (so the layout math stays
                      // simple) but goes invisible; a floating copy follows the
                      // cursor below. Neighbors slide to reveal the drop slot.
                      visibility: isDragged ? 'hidden' : undefined,
                      transform: rowShift(i),
                      transition: drag && !isDragged ? 'transform 150ms ease' : undefined,
                    }}
                    className="mb-1.5 flex items-center justify-between gap-x-2 rounded-[9px] border border-line bg-panel px-3 py-2.5 text-[14px] text-ink"
                  >
                    <span className="flex min-w-0 items-center gap-x-2">
                      <span
                        className="cursor-grab touch-none select-none px-1 text-faint active:cursor-grabbing"
                        aria-hidden
                        onPointerDown={(e) => onHandleDown(e, i)}
                      >
                        ⠿
                      </span>
                      <span className="truncate">{labelOf(key)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(key)}
                      className="grid h-6 w-6 flex-none place-items-center rounded-[6px] text-faint transition-colors hover:bg-surface hover:text-ink"
                      aria-label={`Remove ${labelOf(key)}`}
                    >
                      <CloseIcon className="h-[14px] w-[14px]" />
                    </button>
                  </div>
                )
              })}
              {/* Floating copy of the grabbed row, tracking the pointer. */}
              {drag && (
                <div
                  style={{ position: 'absolute', top: drag.y, left: 0, right: 4, zIndex: 10 }}
                  className="pointer-events-none flex items-center justify-between gap-x-2 rounded-[9px] border border-primary bg-panel px-3 py-2.5 text-[14px] text-ink shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                >
                  <span className="flex min-w-0 items-center gap-x-2">
                    <span className="px-1 text-faint" aria-hidden>⠿</span>
                    <span className="truncate">{labelOf(selected[drag.from])}</span>
                  </span>
                </div>
              )}
              {selected.length === 0 && (
                <div className="rounded-[9px] border border-dashed border-line px-3 py-6 text-center text-[13px] text-faint">
                  No columns selected — pick some from the left.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3 [border-top:1px_solid_var(--border)] px-5 py-3.5">
          <div className="flex items-center gap-x-4">
            <button
              type="button"
              onClick={() => setSelected([])}
              className="inline-flex items-center gap-x-1.5 text-[13px] font-semibold text-dim transition-colors hover:text-danger"
            >
              <TrashIcon className="h-4 w-4" />
              Remove all columns
            </button>
            <label className="flex items-center gap-x-2 text-[13px] text-dim">
              Rows per page
              <Select
                value={String(size)}
                onChange={(v) => setSize(Number(v))}
                ariaLabel="Rows per page"
                buttonClassName="!py-[6px] !px-2.5"
                options={sizeOptions}
              />
            </label>
          </div>
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-[9px] border border-line bg-surface px-[14px] py-[9px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={selected.length === 0}
              onClick={() => selected.length && onSave({ visibleKeys: selected, pageSize: size })}
              title={selected.length === 0 ? 'Select at least one column' : undefined}
              className="inline-flex items-center rounded-[9px] bg-primary px-[16px] py-[9px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
